import * as THREE from "three";
import { ColladaLoader, GLTFLoader, OBJLoader, STLLoader } from "three-stdlib";
import {
  getSharedHeadlessWebglThreeRenderer,
  runHeadlessExclusive,
} from "../utils/sharedHeadlessWebglThree";
import { getAssetActions, getAssetById } from "../stores/assetStore";
import type { AssetEntry } from "../stores/assetStore";
import { getBytes } from "../utils/idb";
import { logger } from "../utils/logger";

type MeshResource = THREE.BufferGeometry | THREE.Group;
type DisposableMaterial = THREE.Material & { dispose: () => void };
type MeshLikeObject = THREE.Object3D & {
  geometry?: THREE.BufferGeometry;
  material?: THREE.Material | THREE.Material[];
};
type MeshTextureCoordinateStatus = {
  state: "unknown" | "authored" | "generated" | "missing";
  vertexCount?: number;
  allocatedBytes?: number;
  reason?: "too-large";
};

const PREVIEW_COLOR = 0x2563eb; // matches UI preview

const hasThreeFlag = (resource: unknown, flag: string): boolean =>
  typeof resource === "object" &&
  resource !== null &&
  (resource as Record<string, unknown>)[flag] === true;

const isBufferGeometry = (resource: unknown): resource is THREE.BufferGeometry =>
  hasThreeFlag(resource, "isBufferGeometry");

const isTexture = (resource: unknown): resource is THREE.Texture =>
  hasThreeFlag(resource, "isTexture");

const isObject3D = (resource: unknown): resource is THREE.Object3D =>
  hasThreeFlag(resource, "isObject3D");

const isMeshObject = (resource: unknown): resource is THREE.Mesh =>
  hasThreeFlag(resource, "isMesh");

async function generateMeshThumbnailDataURL(
  resource: MeshResource,
  size = 192
): Promise<string | null> {
  return runHeadlessExclusive(() => {
    try {
      const thumbRenderer = getSharedHeadlessWebglThreeRenderer();
      const thumbCanvas = thumbRenderer.domElement;
      thumbRenderer.setPixelRatio(1);
      thumbRenderer.outputColorSpace = THREE.SRGBColorSpace;
      thumbRenderer.toneMapping = THREE.ACESFilmicToneMapping;
      thumbRenderer.toneMappingExposure = 1.0;
      thumbRenderer.setClearColor(0xe5e7eb, 1);

      const scene = new THREE.Scene();
      let target: THREE.Object3D | null = null;

      if (isBufferGeometry(resource)) {
        const mat = new THREE.MeshStandardMaterial({
          color: PREVIEW_COLOR,
          side: THREE.DoubleSide,
          metalness: 0.0,
          roughness: 0.6,
        });
        target = new THREE.Mesh(resource, mat);
      } else if (isObject3D(resource)) {
        const cloned = resource.clone(true);
        cloned.traverse((child) => {
          if (isMeshObject(child)) {
            child.material = new THREE.MeshStandardMaterial({
              color: PREVIEW_COLOR,
              side: THREE.DoubleSide,
              metalness: 0.0,
              roughness: 0.6,
            });
          }
        });
        target = cloned;
      }
      if (!target) return null;

      const group = new THREE.Group();
      group.add(target);
      const bbox = new THREE.Box3().setFromObject(group);
      const sizeV = new THREE.Vector3();
      const center = new THREE.Vector3();
      bbox.getSize(sizeV);
      bbox.getCenter(center);
      group.position.sub(center);
      group.updateMatrixWorld(true);
      scene.add(group);

      const maxDim = Math.max(sizeV.x, sizeV.y, sizeV.z) || 1;
      const dist = Math.max(0.1, maxDim * 2.5);
      const cam = new THREE.PerspectiveCamera(35, 1, Math.max(0.01, dist / 1000), dist * 10);
      cam.position.set(dist, dist, dist);
      cam.lookAt(0, 0, 0);
      cam.updateProjectionMatrix();
      scene.add(new THREE.HemisphereLight(0xffffff, 0x888888, 2.0));
      const dir = new THREE.DirectionalLight(0xffffff, 2.0);
      dir.position.set(5, 5, 5);
      scene.add(dir);

      const DPR = 1;
      const SIZE = Math.max(64, Math.min(256, size));
      const width = SIZE * DPR;
      const height = SIZE * DPR;
      thumbCanvas.width = width;
      thumbCanvas.height = height;
      thumbRenderer.setSize(width, height, false);
      thumbRenderer.render(scene, cam);
      thumbRenderer.setRenderTarget(null);
      return thumbCanvas.toDataURL("image/png");
    } catch {
      return null;
    }
  });
}

type TextureResource = THREE.Texture;
type Resource = MeshResource | TextureResource;

const inflight = new Map<string, Promise<Resource>>();
const inflightWaiters = new Map<string, number>();
const cache = new Map<string, Resource>();
const refs = new Map<string, number>();
const objectUrls = new Map<string, string>(); // id -> objectURL we created
const meshTextureCoordinateStatus = new Map<string, MeshTextureCoordinateStatus>();

let objLoader: OBJLoader | null = null;
let stlLoader: STLLoader | null = null;
let colladaLoader: ColladaLoader | null = null;
let gltfLoader: GLTFLoader | null = null;
let texLoader: THREE.TextureLoader | null = null;

function getObjLoader() {
  if (!objLoader) objLoader = new OBJLoader();
  return objLoader;
}

function getStlLoader() {
  if (!stlLoader) stlLoader = new STLLoader();
  return stlLoader;
}

function getColladaLoader() {
  if (!colladaLoader) colladaLoader = new ColladaLoader();
  return colladaLoader;
}

function getGltfLoader() {
  if (!gltfLoader) gltfLoader = new GLTFLoader();
  return gltfLoader;
}

function getTexLoader() {
  if (!texLoader) texLoader = new THREE.TextureLoader();
  return texLoader;
}

function undoColladaZUpSceneRotation(scene: THREE.Object3D): void {
  const zUpLoaderQuat = new THREE.Quaternion().setFromEuler(new THREE.Euler(-Math.PI / 2, 0, 0));
  if (scene.quaternion.angleTo(zUpLoaderQuat) < 1e-6) {
    scene.quaternion.identity();
    scene.updateMatrix();
    scene.updateMatrixWorld(true);
  }
}

function filterLoadedMeshes(
  root: unknown,
  label: string,
  options: { preserveHierarchy?: boolean } = {}
): THREE.Group {
  if (!isObject3D(root)) {
    throw new Error(`[assetCache] Mesh loader returned no traversable object for ${label}`);
  }

  const meshes: THREE.Mesh[] = [];
  root.traverse((child) => {
    if (isMeshObject(child)) {
      meshes.push(child);
    }
  });

  if (meshes.length === 0) {
    throw new Error(`[assetCache] Mesh asset ${label} did not contain mesh geometry`);
  }

  if (options.preserveHierarchy) {
    const grouped = new THREE.Group();
    grouped.add(root);
    return grouped;
  }

  const filtered = new THREE.Group();
  for (const mesh of meshes) {
    filtered.add(mesh);
  }
  return filtered;
}

function incRefsOnResolve(id: string) {
  const waiters = inflightWaiters.get(id) || 1;
  refs.set(id, (refs.get(id) || 0) + waiters);
  inflightWaiters.delete(id);
}

function disposeResource(res: Resource) {
  if (!res) return;
  // Texture
  if (isTexture(res)) {
    res.dispose();
    return;
  }
  // Group or Mesh/Geometry
  if (isBufferGeometry(res)) {
    res.dispose();
    return;
  }
  // If it's a Group or Object3D containing meshes
  if (isObject3D(res)) {
    res.traverse((child) => {
      const mesh = child as MeshLikeObject;
      if (mesh.geometry) mesh.geometry.dispose();
      if (mesh.material) {
        if (Array.isArray(mesh.material))
          mesh.material.forEach((material: DisposableMaterial | null | undefined) =>
            material?.dispose()
          );
        else mesh.material.dispose();
      }
    });
  }
}

async function getUrlForEntry(id: string, entry: AssetEntry): Promise<string> {
  const sources = entry.sources || {};

  if (sources.blobUrl) {
    if (sources.blobUrl.startsWith("blob:")) {
      objectUrls.set(id, sources.blobUrl);
    }
    return sources.blobUrl;
  }

  if (sources.idbKey) {
    try {
      const bytes = await getBytes(sources.idbKey);
      if (bytes) {
        const blob = bytes instanceof Blob ? bytes : new Blob([bytes]);
        const url = URL.createObjectURL(blob);
        objectUrls.set(id, url);
        return url;
      }
    } catch {
      // fall through to other sources
    }
  }

  if (sources.url) {
    if (sources.url.startsWith("blob:")) {
      objectUrls.set(id, sources.url);
    }
    return sources.url;
  }

  throw new Error(`[assetCache] No available source for asset ${id}`);
}

async function loadMeshByUrl(url: string, originalName?: string): Promise<MeshResource> {
  // Decide loader by file extension (check original name first for blob URLs)
  const lower = (originalName || url).toLowerCase();
  if (lower.endsWith(".stl") || lower.includes(".stl?") || lower.includes(".stl")) {
    const loader = getStlLoader();
    return new Promise<MeshResource>((resolve, reject) => {
      loader.load(
        url,
        (geometry) => resolve(geometry),
        undefined,
        (err) => reject(err)
      );
    });
  }
  if (lower.endsWith(".dae") || lower.includes(".dae?") || lower.includes(".dae")) {
    const loader = getColladaLoader();
    return new Promise<MeshResource>((resolve, reject) => {
      loader.load(
        url,
        (collada) => {
          try {
            undoColladaZUpSceneRotation(collada.scene);
            resolve(
              filterLoadedMeshes(collada.scene, originalName || url, { preserveHierarchy: true })
            );
          } catch (error) {
            reject(error);
          }
        },
        undefined,
        (err) => reject(err)
      );
    });
  }
  if (
    lower.endsWith(".glb") ||
    lower.includes(".glb?") ||
    lower.includes(".glb") ||
    lower.endsWith(".gltf") ||
    lower.includes(".gltf?") ||
    lower.includes(".gltf")
  ) {
    const loader = getGltfLoader();
    return new Promise<MeshResource>((resolve, reject) => {
      loader.load(
        url,
        (gltf) => {
          try {
            resolve(
              filterLoadedMeshes(gltf.scene, originalName || url, { preserveHierarchy: true })
            );
          } catch (error) {
            reject(error);
          }
        },
        undefined,
        (err) => reject(err)
      );
    });
  }
  // OBJ: sanitize and filter out non-mesh primitives (lines/points)
  const loader = getObjLoader();
  try {
    const res = await fetch(url);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const txt = await res.text();
    // Remove line elements that produce LineSegments artifacts
    const sanitized = txt.replace(/^\s*l\s+.*$/gim, "");
    const parsed = loader.parse(sanitized) as unknown as THREE.Object3D;
    return filterLoadedMeshes(parsed, originalName || url);
  } catch {
    // Fallback to loader.load if fetch/parse fails
    return new Promise<MeshResource>((resolve, reject) => {
      loader.load(
        url,
        (group) => {
          try {
            resolve(filterLoadedMeshes(group, originalName || url));
          } catch (error) {
            reject(error);
          }
        },
        undefined,
        (err) => reject(err)
      );
    });
  }
}

/**
 * Generates box-projection UV coordinates for geometries that have none.
 * Selects the projection plane per-vertex based on the dominant normal axis,
 * giving reasonable results on mechanical/robot meshes without hand-authored UVs.
 * UVs are in world-space units so tiling repeat in the material editor scales them.
 */
const MAX_GENERATED_UV_VERTICES = 250_000;
const textureCoordinateToastShownForAssetIds = new Set<string>();
const MESH_TEXTURE_COORDINATE_STATUS_EVENT = "critter-mesh-texture-coordinate-status";

function getTextureCoordinateWarningText(vertexCount?: number): string {
  const countText = Number.isFinite(vertexCount)
    ? ` This mesh has ${vertexCount?.toLocaleString()} vertices.`
    : "";
  return `Texture images may not appear correctly because this mesh does not include texture coordinates.${countText} Add texture coordinates in your modeling tool or simplify the mesh so Critter can generate them automatically.`;
}

export function getMeshTextureCoordinateStatus(id: string): MeshTextureCoordinateStatus {
  return meshTextureCoordinateStatus.get(id) || { state: "unknown" };
}

export function getMeshTextureCoordinateWarning(id: string): string | null {
  const status = getMeshTextureCoordinateStatus(id);
  if (status.state !== "missing") return null;
  return getTextureCoordinateWarningText(status.vertexCount);
}

function showMeshTextureCoordinateToast(id: string, status: MeshTextureCoordinateStatus): void {
  if (typeof window === "undefined") return;
  if (textureCoordinateToastShownForAssetIds.has(id)) return;
  textureCoordinateToastShownForAssetIds.add(id);
  const countText = Number.isFinite(status.vertexCount)
    ? ` (${status.vertexCount?.toLocaleString()} vertices)`
    : "";
  const message = `Large mesh has no UVs${countText}; textures may look wrong.`;
  try {
    window.dispatchEvent(
      new CustomEvent("critter-toast", {
        detail: { message, tone: "warning" },
      })
    );
  } catch {
    // Toasts are best-effort UI feedback.
  }
}

function dispatchMeshTextureCoordinateStatusEvent(id: string): void {
  if (typeof window === "undefined") return;
  try {
    window.dispatchEvent(
      new CustomEvent(MESH_TEXTURE_COORDINATE_STATUS_EVENT, {
        detail: { id },
      })
    );
  } catch {
    // Cache status events are best-effort UI invalidation.
  }
}

export function subscribeMeshTextureCoordinateStatus(id: string, listener: () => void): () => void {
  if (typeof window === "undefined") return () => {};
  const handler = (event: Event) => {
    const detail = (event as CustomEvent<{ id?: string }>).detail;
    if (detail?.id === id) listener();
  };
  window.addEventListener(MESH_TEXTURE_COORDINATE_STATUS_EVENT, handler);
  return () => window.removeEventListener(MESH_TEXTURE_COORDINATE_STATUS_EVENT, handler);
}

function rankTextureCoordinateStatus(status: MeshTextureCoordinateStatus): number {
  if (status.state === "missing") return 4;
  if (status.state === "generated") return 3;
  if (status.state === "authored") return 2;
  return 1;
}

function generateBoxUVsIfMissing(geometry: THREE.BufferGeometry): MeshTextureCoordinateStatus {
  if (geometry.attributes.uv) return { state: "authored" };
  const positions = geometry.attributes.position;
  if (!positions) return { state: "unknown" };
  const normals = geometry.attributes.normal;
  const count = positions.count;
  if (count > MAX_GENERATED_UV_VERTICES) {
    const allocatedBytes = count * 2 * Float32Array.BYTES_PER_ELEMENT;
    logger.warn(
      `[assetCache] Skipping generated UVs for large mesh (${count} vertices). This avoids allocating an extra ${(allocatedBytes / 1024 / 1024).toFixed(1)} MB typed array; textures may not project on this mesh until UVs are authored or the mesh is simplified.`
    );
    return { state: "missing", vertexCount: count, allocatedBytes, reason: "too-large" };
  }
  const uvArray = new Float32Array(count * 2);
  for (let i = 0; i < count; i++) {
    const x = positions.getX(i);
    const y = positions.getY(i);
    const z = positions.getZ(i);
    let u: number, v: number;
    if (normals) {
      const nx = Math.abs(normals.getX(i));
      const ny = Math.abs(normals.getY(i));
      const nz = Math.abs(normals.getZ(i));
      if (nx >= ny && nx >= nz) {
        u = z;
        v = y; // YZ plane (X-dominant face)
      } else if (ny >= nx && ny >= nz) {
        u = x;
        v = z; // XZ plane (Y-dominant face)
      } else {
        u = x;
        v = y; // XY plane (Z-dominant face)
      }
    } else {
      u = x;
      v = y;
    }
    uvArray[i * 2] = u;
    uvArray[i * 2 + 1] = v;
  }
  geometry.setAttribute("uv", new THREE.BufferAttribute(uvArray, 2));
  return { state: "generated", vertexCount: count, allocatedBytes: uvArray.byteLength };
}

function applyBoxUVsToResource(id: string, res: MeshResource): void {
  let aggregate: MeshTextureCoordinateStatus = { state: "unknown" };
  const merge = (nextStatus: MeshTextureCoordinateStatus) => {
    if (rankTextureCoordinateStatus(nextStatus) > rankTextureCoordinateStatus(aggregate)) {
      aggregate = nextStatus;
    }
  };

  if (isBufferGeometry(res)) {
    merge(generateBoxUVsIfMissing(res));
  } else if (isObject3D(res)) {
    res.traverse((child) => {
      if (isMeshObject(child) && child.geometry) {
        merge(generateBoxUVsIfMissing(child.geometry));
      }
    });
  }
  meshTextureCoordinateStatus.set(id, aggregate);
  dispatchMeshTextureCoordinateStatusEvent(id);
  if (aggregate.state === "missing") {
    showMeshTextureCoordinateToast(id, aggregate);
  }
}

async function loadTextureByUrl(url: string): Promise<TextureResource> {
  const loader = getTexLoader();
  return new Promise<TextureResource>((resolve, reject) => {
    loader.load(
      url,
      (tex) => resolve(tex),
      undefined,
      (err) => reject(err)
    );
  });
}

async function loadMeshInternal(id: string): Promise<MeshResource> {
  const entry = getAssetById(id);
  if (!entry) throw new Error(`[assetCache] Asset not found: ${id}`);
  getAssetActions().setAssetStatus(id, "loading");

  const url = await getUrlForEntry(id, entry);
  // Pass original name so loader can detect STL vs OBJ even for blob URLs
  const res = await loadMeshByUrl(url, entry.name);

  // Ensure every geometry has UV coordinates so textures render correctly
  // on meshes that were exported without UVs (e.g. OBJ/STL/glTF from URDF pipelines).
  applyBoxUVsToResource(id, res);

  // Mark ready, capture simple meta
  if (isBufferGeometry(res)) {
    const vertexCount = (res.attributes?.position?.count as number) || undefined;
    if (vertexCount !== undefined) {
      getAssetActions().setMeshVertexCount(id, vertexCount);
    }
  }

  getAssetActions().setAssetStatus(id, "ready", { error: null });

  // Generate and persist a thumbnail for meshes when they are first loaded into the scene
  try {
    if (entry.kind === "mesh" && entry.name) {
      const actions = getAssetActions();
      const persisted = typeof entry.thumbnailDataUrl === "string" ? entry.thumbnailDataUrl : null;
      const persistedOk = persisted && persisted.startsWith("data:");
      if (!persistedOk) {
        const dataUrl = await generateMeshThumbnailDataURL(res as MeshResource, 192);
        if (dataUrl) {
          try {
            actions.setAssetThumbnail(id, dataUrl);
          } catch (error) {
            logger.warn("[assetCache] Failed to set asset thumbnail", error);
          }
        }
      }
    }
  } finally {
    // no-op
  }
  return res;
}

async function loadTextureInternal(id: string): Promise<TextureResource> {
  const entry = getAssetById(id);
  if (!entry) throw new Error(`[assetCache] Asset not found: ${id}`);
  getAssetActions().setAssetStatus(id, "loading");

  const url = await getUrlForEntry(id, entry);
  const tex = await loadTextureByUrl(url);
  tex.colorSpace = THREE.SRGBColorSpace; // default for color maps
  getAssetActions().setAssetStatus(id, "ready", { error: null });
  return tex;
}

export async function acquireMesh(id: string): Promise<MeshResource> {
  // Cache hit
  if (cache.has(id)) {
    refs.set(id, (refs.get(id) || 0) + 1);
    return cache.get(id) as MeshResource;
  }

  // Inflight
  if (inflight.has(id)) {
    inflightWaiters.set(id, (inflightWaiters.get(id) || 0) + 1);
    return inflight.get(id)!.then((res) => {
      incRefsOnResolve(id);
      return res as MeshResource;
    });
  }

  // Start new load
  inflightWaiters.set(id, 1);
  const p = loadMeshInternal(id)
    .then((res) => {
      cache.set(id, res);
      incRefsOnResolve(id);
      return res;
    })
    .catch((e) => {
      getAssetActions().setAssetStatus(id, "error", { error: String(e) });
      throw e;
    })
    .finally(() => {
      inflight.delete(id);
    });
  inflight.set(id, p);
  return p as Promise<MeshResource>;
}

export async function acquireTexture(id: string): Promise<TextureResource> {
  if (cache.has(id)) {
    refs.set(id, (refs.get(id) || 0) + 1);
    return cache.get(id) as TextureResource;
  }
  if (inflight.has(id)) {
    inflightWaiters.set(id, (inflightWaiters.get(id) || 0) + 1);
    return inflight.get(id)!.then((res) => {
      incRefsOnResolve(id);
      return res as TextureResource;
    });
  }
  inflightWaiters.set(id, 1);
  const p = loadTextureInternal(id)
    .then((res) => {
      cache.set(id, res);
      incRefsOnResolve(id);
      return res;
    })
    .catch((e) => {
      getAssetActions().setAssetStatus(id, "error", { error: String(e) });
      throw e;
    })
    .finally(() => {
      inflight.delete(id);
    });
  inflight.set(id, p);
  return p as Promise<TextureResource>;
}

export function release(id: string): void {
  const r = (refs.get(id) || 0) - 1;
  if (r > 0) {
    refs.set(id, r);
    return;
  }
  refs.delete(id);
  const res = cache.get(id);
  if (res) {
    disposeResource(res);
    cache.delete(id);
  }
  const url = objectUrls.get(id);
  if (url) {
    try {
      URL.revokeObjectURL(url);
    } catch (error) {
      logger.warn("[assetCache] Failed to revoke object URL during release", error);
    }
    objectUrls.delete(id);
  }
}

export function disposeAll(): void {
  // Dispose resources
  cache.forEach((res) => disposeResource(res));
  cache.clear();
  refs.clear();
  // Revoke object URLs
  objectUrls.forEach((url) => {
    try {
      URL.revokeObjectURL(url);
    } catch (error) {
      logger.warn("[assetCache] Failed to revoke object URL during disposeAll", error);
    }
  });
  objectUrls.clear();
  meshTextureCoordinateStatus.clear();
  // Clear inflight (cannot cancel promises, but clear references)
  inflight.clear();
  inflightWaiters.clear();
}

export function getStats() {
  return {
    cacheSize: cache.size,
    refsSize: refs.size,
    inflightSize: inflight.size,
    urlsTracked: objectUrls.size,
  };
}
